import { isRequestCancellationError } from './auth';
import { extractChatErrorSignals, isHardChatAuthFailure } from './chatError';
import { getTokenRequestDiagnostics } from './tokenResponseDiagnostics';
import { TOKEN_RECOVERY_BACKOFF_MS, TOKEN_RECOVERY_MAX_EXECUTIONS } from './tokenRecoveryConfig';

export {
  TOKEN_RECOVERY_BACKOFF_MS,
  TOKEN_RECOVERY_MAX_EXECUTIONS,
  TOKEN_RECOVERY_EPISODE_TIMEOUT_MS
} from './tokenRecoveryConfig';

export type TokenFailureDisposition = 'cancelled' | 'non_retryable' | 'retryable' | 'terminal';

const RETRYABLE_ERROR_CODES = new Set(['ECONNABORTED', 'ERR_NETWORK', 'ETIMEDOUT']);

export const getTokenFailureDisposition = (error: unknown): TokenFailureDisposition => {
  if (isRequestCancellationError(error)) {
    return 'cancelled';
  }

  const diagnostics = getTokenRequestDiagnostics(error);

  if (diagnostics) {
    if (diagnostics.transport === 'cancelled') {
      return 'cancelled';
    }

    if (diagnostics.status === 401 || diagnostics.status === 403) {
      return 'terminal';
    }

    if (isHardChatAuthFailure(error)) {
      return 'terminal';
    }

    if (diagnostics.failureAt === 'transport') {
      return diagnostics.transport === 'watchdog_timeout' || diagnostics.transport === 'no_response'
        ? 'retryable'
        : 'non_retryable';
    }

    return diagnostics.failureAt === 'http' && diagnostics.status && diagnostics.status >= 500
      ? 'retryable'
      : 'non_retryable';
  }

  if (isHardChatAuthFailure(error)) {
    return 'terminal';
  }

  const { codes, names, statuses } = extractChatErrorSignals(error);

  if (statuses.some(status => status === 401 || status === 403)) {
    return 'terminal';
  }

  if (statuses.some(status => status >= 500)) {
    return 'retryable';
  }

  if (names.includes('TimeoutError') || codes.some(code => RETRYABLE_ERROR_CODES.has(code))) {
    return 'retryable';
  }

  return 'non_retryable';
};

export const getTokenRecoveryDelayMs = (failedExecutions: number): number | null => {
  if (failedExecutions < 1 || failedExecutions >= TOKEN_RECOVERY_MAX_EXECUTIONS) {
    return null;
  }

  return TOKEN_RECOVERY_BACKOFF_MS[failedExecutions - 1] ?? null;
};

export const waitForTokenRecoveryDelay = (
  signal: AbortSignal,
  delayMs: number
): Promise<boolean> => {
  if (signal.aborted) {
    return Promise.resolve(false);
  }

  return new Promise(resolve => {
    let timeout: number;
    const handleAbort = () => {
      clearTimeout(timeout);
      signal.removeEventListener('abort', handleAbort);
      resolve(false);
    };

    timeout = window.setTimeout(() => {
      signal.removeEventListener('abort', handleAbort);
      resolve(true);
    }, delayMs);

    signal.addEventListener('abort', handleAbort, { once: true });
  });
};
